import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import {
  Package, Warehouse, ChefHat, Factory, Users, ShoppingCart,
  AlertTriangle, TrendingUp, ArrowUpRight, Inbox, CheckCircle2,
  PackagePlus, Repeat, ClipboardList, FileText, UtensilsCrossed, Wallet, Receipt
} from 'lucide-react';

// HIZLI ISLEMLER — hepsi mevcut sayfalara kisayol, yeni ekran yok.
// `permission`: islem YARATMA oldugu icin 'view' degil 'edit' araniyor.
const hizliIslemler = [
  { label: 'Ürün Ekle', ipucu: 'Yeni ürün kartı', icon: PackagePlus,
    path: '/erp/products/new', color: '#3d6e4e', permission: 'products' },
  { label: 'Transfer Yap', ipucu: 'Depolar arası', icon: Repeat,
    path: '/erp/transfers', color: '#2e6fa0', permission: 'stock_movements' },
  { label: 'Sayım Yap', ipucu: 'Yeni stok sayımı', icon: ClipboardList,
    path: '/erp/stock/counts/new', color: '#7a6a2e', permission: 'stock_counts' },
  { label: 'Sipariş Talebi', ipucu: 'Yeni satın alma talebi', icon: FileText,
    path: '/erp/purchase-requests/new', color: '#7a3535', permission: 'purchasing' },
  { label: 'Üretim Oluştur', ipucu: 'Reçeteli üretim emri', icon: Factory,
    path: '/erp/production-orders/new', color: '#5a4e8a', permission: 'production_orders' },
  { label: 'Tüketim Oluştur', ipucu: 'Reçetesiz tüketim', icon: UtensilsCrossed,
    path: '/erp/consumptions/new', color: '#8a5a2e', permission: 'production_orders' },
  { label: 'Hesap Hareketi', ipucu: 'Kasa ve banka', icon: Wallet,
    path: '/erp/treasury', color: '#2e7a6a', permission: 'treasury' },
  { label: 'Fatura Girişi', ipucu: 'Yeni alış faturası', icon: Receipt,
    path: '/erp/invoices/new', color: '#6b4c2e', permission: 'invoices' },
];

const modules = [
  { label: 'Ürün Kartları', desc: 'Ürün yönetimi ve stok takibi', icon: Package, path: '/erp/products', color: '#3d6e4e' },
  { label: 'Depolar', desc: 'Depo yönetimi ve stok görünümü', icon: Warehouse, path: '/erp/warehouses', color: '#2e6fa0' },
  { label: 'Reçeteler', desc: 'Üretim reçeteleri ve maliyetler', icon: ChefHat, path: '/erp/recipes', color: '#7a6a2e' },
  { label: 'Üretim Emirleri', desc: 'Üretim planlama ve takip', icon: Factory, path: '/erp/production-orders', color: '#5a4e8a' },
  { label: 'Tedariçkiler', desc: 'Tedarikçi ve cari hesap yönetimi', icon: Users, path: '/erp/suppliers', color: '#6b4c2e' },
  { label: 'Satın Alma', desc: 'Talep, sipariş ve mal kabul', icon: ShoppingCart, path: '/erp/purchasing', color: '#7a3535' },
  { label: 'Dosya Kutusu', desc: 'İşlenmeyi bekleyen belgeler', icon: Inbox, path: '/erp/dosya-kutusu', color: '#2e6fa0', permission: 'dosya_kutusu' },
];

const statConfig = [
  { key: 'total_products', label: 'Toplam Ürün', icon: Package, color: '#3d6e4e', accentColor: '#3d6e4e' },
  { key: 'total_recipes', label: 'Aktif Reçete', icon: ChefHat, color: '#7a6a2e', accentColor: '#7a6a2e' },
  { key: 'pending_requests', label: 'Bekleyen Talep', icon: ShoppingCart, color: '#2e6fa0', accentColor: '#2e6fa0' },
  { key: 'low_stock_count', label: 'Düşük Stok', icon: AlertTriangle, colorFn: (v) => v > 0 ? '#7a3535' : '#3d6e4e', accentColorFn: (v) => v > 0 ? '#7a3535' : '#3d6e4e' },
];

export default function Dashboard() {
  const { company, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [kutu, setKutu] = useState(null);

  // Kutuyu görme yetkisi yoksa istek de atılmaz (gereksiz 403 olmasın)
  const kutuGorulur = hasPermission ? hasPermission('dosya_kutusu', 'view') : false;

  useEffect(() => {
    api.get('/dashboard/stats')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    if (!kutuGorulur) return;
    api.get('/dosya-kutusu/sayilar').then(r => setKutu(r.data)).catch(() => {});
  }, [kutuGorulur]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Günaydın';
    if (h < 18) return 'İyi günler';
    return 'İyi akşamlar';
  })();

  return (
    <div data-testid="dashboard">
      {/* Header */}
      <div className="mb-8">
        <p className="text-sm font-medium text-[#8a7e70] mb-1">{greeting} 👋</p>
        <h1 className="text-3xl font-extrabold text-[#1a1916] tracking-tight">
          Kontrol Paneli
        </h1>
        {company && (
          <p className="text-[#8a7e70] mt-1 text-sm">
            {company.name}
          </p>
        )}
      </div>

      {/* DOSYA KUTUSU — panelin en üstü.
          Kutunun boşalması bir görev ölçüsü; paneli açan kişi önce bunu
          görmeli. Dolu ise dikkat çekici, boş ise sakin. */}
      {kutuGorulur && kutu && (
        <Link to="/erp/dosya-kutusu" data-testid="panel-dosya-kutusu" className="block mb-6">
          <Card className={`shadow-sm erp-card cursor-pointer group overflow-hidden
            ${(kutu.bekliyor || 0) + (kutu.duzeltme || 0) > 0
              ? 'bg-[#faf7f2] border-[#2e6fa0]/40'
              : 'bg-[#faf7f2] border-[#d0c4b0]'}`}>
            <div className="h-[3px] w-full"
              style={{ backgroundColor: (kutu.bekliyor || 0) > 0 ? '#2e6fa0' : '#3d6e4e', opacity: 0.6 }} />
            <CardContent className="p-4 md:p-5 flex items-center gap-4">
              <div className="w-11 h-11 md:w-12 md:h-12 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: (kutu.bekliyor || 0) > 0 ? '#2e6fa018' : '#3d6e4e18' }}>
                {(kutu.bekliyor || 0) > 0
                  ? <Inbox size={22} strokeWidth={1.6} style={{ color: '#2e6fa0' }} />
                  : <CheckCircle2 size={22} strokeWidth={1.6} style={{ color: '#3d6e4e' }} />}
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-bold text-[#1a1916]">Dosya Kutusu</h3>
                {(kutu.bekliyor || 0) > 0 ? (
                  <p className="text-xs text-[#8a7e70] mt-0.5">
                    <strong className="text-[#2e6fa0]">{kutu.bekliyor}</strong> belge işlenmeyi bekliyor
                    {(kutu.duzeltme || 0) > 0 && (
                      <span className="text-[#7a3535]"> · {kutu.duzeltme} düzeltme bekliyor</span>
                    )}
                    {(kutu.isleniyor || 0) > 0 && (
                      <span> · {kutu.isleniyor} işleniyor</span>
                    )}
                  </p>
                ) : (kutu.duzeltme || 0) > 0 ? (
                  <p className="text-xs text-[#7a3535] mt-0.5">
                    {kutu.duzeltme} belge düzeltme bekliyor
                  </p>
                ) : (
                  <p className="text-xs text-[#8a7e70] mt-0.5">Kutu boş — işlenecek belge yok</p>
                )}
              </div>

              {/* Bekleyen sayısı iri: uzaktan bakınca da okunsun */}
              {(kutu.bekliyor || 0) > 0 && (
                <p className="text-3xl md:text-4xl font-extrabold leading-none shrink-0"
                  style={{ color: '#2e6fa0' }} data-testid="panel-kutu-sayisi">
                  {kutu.bekliyor}
                </p>
              )}
              <ArrowUpRight size={16}
                className="text-[#d0c4b0] group-hover:text-[#3d6e4e] transition-colors shrink-0" />
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {statsLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-4">
                <div className="skeleton h-4 w-24 mb-3" />
                <div className="skeleton h-8 w-16" />
              </div>
            ))
          : statConfig.map((s, i) => {
              const Icon = s.icon;
              const value = stats?.[s.key] ?? 0;
              const color = s.colorFn ? s.colorFn(value) : s.color;
              const accent = s.accentColorFn ? s.accentColorFn(value) : s.accentColor;
              return (
                <Card key={i} className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm animate-fade-in overflow-hidden">
                  <div className="h-[3px] w-full" style={{ backgroundColor: accent, opacity: 0.6 }} />
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold text-[#8a7e70] uppercase tracking-wide">{s.label}</p>
                      <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: color + '18' }}>
                        <Icon size={14} strokeWidth={1.8} style={{ color }} />
                      </div>
                    </div>
                    <p className="text-3xl font-extrabold text-[#1a1916] leading-none">{value}</p>
                  </CardContent>
                </Card>
              );
            })
        }
      </div>

      {/* ── HIZLI İŞLEMLER ── */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-[#8a7e70]">Hızlı İşlemler</h2>
        <div className="flex-1 h-px bg-[#d0c4b0]" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {hizliIslemler
          .filter(a => !a.permission || (hasPermission ? hasPermission(a.permission, 'edit') : true))
          .map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.path} to={a.path} data-testid={`hizli-islem-${a.path}`}>
                <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm erp-card cursor-pointer
                                 group h-full overflow-hidden relative">
                  {/* Soldaki renk seridi: sekiz karo tek blok gibi okunmasin */}
                  <div className="absolute left-0 top-0 bottom-0 w-[3px]"
                    style={{ backgroundColor: a.color }} />
                  <CardContent className="p-3.5 pl-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0
                                    transition-transform duration-200 group-hover:scale-105"
                      style={{ backgroundColor: a.color + '16' }}>
                      <Icon size={18} strokeWidth={1.8} style={{ color: a.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      {/* Mobilde iki sutuna dusunce "Siparis Tal..." gibi kirpiliyordu;
                          etiket asil bilgi oldugu icin kirpmak yerine alt satira gecsin.
                          Masaustunde zaten tek satira sigiyor. */}
                      <p className="text-sm font-bold text-[#1a1916] leading-tight break-words">{a.label}</p>
                      <p className="text-[11px] text-[#8a7e70] truncate">{a.ipucu}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
      </div>

      {/* Section label */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-[#8a7e70]">Modüller</h2>
        <div className="flex-1 h-px bg-[#d0c4b0]" />
      </div>

      {/* Module Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
        {modules.filter(m => !m.permission
          || (hasPermission ? hasPermission(m.permission, 'view') : true)).map((mod) => {
          const Icon = mod.icon;
          return (
            <Link key={mod.path} to={mod.path} data-testid={`module-card-${mod.path}`}>
              <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm erp-card cursor-pointer group h-full">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: mod.color + '14' }}
                    >
                      <Icon size={20} strokeWidth={1.6} style={{ color: mod.color }} />
                    </div>
                    <ArrowUpRight
                      size={15}
                      className="text-[#d0c4b0] group-hover:text-[#3d6e4e] transition-colors duration-200 mt-0.5"
                    />
                  </div>
                  <h3 className="text-sm font-bold text-[#1a1916] mb-1">{mod.label}</h3>
                  <p className="text-xs text-[#8a7e70] leading-relaxed">{mod.desc}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
